/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { AccessionPrefix } from './AccessionPrefix';
import type { AdminLabOrder } from './AdminLabOrder';
import type { AntibodySpecies } from './AntibodySpecies';
import type { AntigenRetrievalMethod } from './AntigenRetrievalMethod';
import type { Block } from './Block';
import type { CasesAdmin } from './CasesAdmin';
import type { Lab } from './Lab';
import type { LabStation } from './LabStation';
import type { SlideFlag } from './SlideFlag';
import type { SlideStatusChange } from './SlideStatusChange';
import type { Specimen } from './Specimen';
import type { UserName } from './UserName';

export type SlideAdmin = {
    readonly id?: number;
    /**
     * unique identifier for the slide
     */
    uuid?: string;
    readonly case?: CasesAdmin;
    readonly lab?: Lab;
    /**
     * original filename of the slide from the scanner
     */
    name?: string | null;
    readonly created_at?: string;
    /**
     * date slide was scanned by lab, retrieved from slide metadata
     */
    scanned_date?: string | null;
    /**
     * the name of the process that added the slide
     */
    inserted_by?: SlideAdmin.inserted_by;
    /**
     * name of the file stored in S3 generated by the host scanner
     */
    url_path?: string | null;
    /**
     * an md5 hash of the uplaoded s3 filename
     */
    uploader_hash?: string | null;
    /**
     * status of a slide until it's fully imported and visible to a user in 'success'. History logged by SlideStatusChange.
     */
    status?: SlideAdmin.status;
    /**
     * industry name for the extracted 'case id' value. Should match Case.accession
     */
    accession_number?: string | null;
    /**
     * slide number in the provided block
     */
    slide_number?: string | null;
    readonly stain_id?: number;
    readonly specimen?: Specimen;
    readonly specimen_id?: number;
    readonly block_obj?: Block;
    readonly block_obj_id?: number;
    readonly accession_prefix?: AccessionPrefix;
    /**
     * Date upon which the slide's WSI will be removed and tiles will be used exclusively.
     */
    high_res_removal_date?: string | null;
    readonly lab_orders?: Array<AdminLabOrder>;
    readonly lab_order?: AdminLabOrder;
    readonly lab_order_id?: number;
    readonly lab_station?: LabStation;
    lis_state?: SlideAdmin.lis_state | null;
    readonly flags?: Array<SlideFlag>;
    is_rescan?: boolean;
    is_control?: boolean;
    readonly ordering_doctor?: UserName;
    /**
     * operational or temporary notes from the quality control team
     */
    qc_notes?: string | null;
    /**
     * selectable status field for QC slide tracking process
     */
    qc_status?: SlideAdmin.qc_status | null;
    quality_errors?: Array<'missing-tiles' | 'out-of-focus' | 'qc_issue'> | null;
    /**
     * the reason why this slide ended up in manual review statuses
     */
    slide_review_reason?: SlideAdmin.slide_review_reason | null;
    /**
     * datetime that the slide should be uploaded by, based on lab.expected_gross_to_upload_hours
     */
    expected_upload_by?: string | null;
    /**
     * the slide this was copied and then anonymized from
     */
    anonymized_source_slide?: number | null;
    /**
     * If true the slide has been anonymized, removing all PHI
     */
    is_anonymized?: boolean;
    readonly status_changes?: Array<SlideStatusChange>;
    readonly raw_barcode_text?: string;
    /**
     * type of slide: brightfield or fluorescence
     */
    slide_type?: SlideAdmin.slide_type;
    readonly antigen_retrieval_method?: AntigenRetrievalMethod;
    readonly antibody_species?: AntibodySpecies;
}

export namespace SlideAdmin {

    /**
     * the name of the process that added the slide
     */
    export enum inserted_by {
        UPLOADER = 'uploader',
        WPL_IMPORT = 'WPL Import',
        SONIC_IMPORT = 'Sonic Import',
        SCAN_AND_IMPORT_SLIDES = 'scan_and_import_slides',
        WSI_IMPORTER_SCRIPT = 'wsi_importer_script',
        LIS_IMPORT = 'LIS Import',
        SEED_COMMAND = 'Seed Command',
    }

    /**
     * status of a slide until it's fully imported and visible to a user in 'success'. History logged by SlideStatusChange.
     */
    export enum status {
        RECEIVED_WPL = 'received-wpl',
        RECEIVED_LAB = 'received-lab',
        PENDING = 'pending',
        PRECOMPUTING = 'precomputing',
        UPLOADED = 'uploaded',
        ERROR = 'error',
        MANUAL_MERGE = 'manual-merge',
        MANUAL_REVIEW = 'manual-review',
        SUCCESS = 'success',
        PENDING_AI = 'pending-ai',
        DELETED = 'deleted',
        ARCHIVED = 'archived',
        ON_HOLD = 'on-hold',
        PROCESSING = 'processing',
    }

    export enum lis_state {
        UNSTAINED = 'unstained',
        RACKED = 'racked',
        STAINED = 'stained',
        SCANNING = 'scanning',
        SCANNED = 'scanned',
    }

    /**
     * selectable status field for QC slide tracking process
     */
    export enum qc_status {
        CHECKED = 'checked',
        BLURRY = 'blurry',
        CONTACT_LAB = 'contact-lab',
        MISSING_LAB = 'missing-lab',
        MISSING_VIEWER = 'missing-viewer',
        MISLABELED = 'mislabeled',
        UNKNOWN = 'unknown',
    }

    /**
     * the reason why this slide ended up in manual review statuses
     */
    export enum slide_review_reason {
        MISSING_CASE = 'missing-case',
        MISSING_ORDERING_DOCTOR = 'missing-ordering-doctor',
        MISSING_ACTIVE_ASSIGNMENT = 'missing-active-assignment',
        MISSING_ACCESSIONING_LAB = 'missing-accessioning-lab',
        RANDOM_SELECTION = 'random-selection',
        PREFIX_RANDOM_SELECTION = 'prefix-random-selection',
        REIMPORT_METADATA_TASK = 'reimport-metadata-task',
        MISSING_BARCODE = 'missing-barcode',
        SLIDE_HAS_IHC_STAIN = 'slide-has-ihc-stain',
        QUALITY_ISSUE_FOUND = 'quality-issue-found',
        AUTO_CREATED_CASE = 'auto-created-case',
        MISSING_STAIN = 'missing-stain',
        NOT_GROSSED = 'not-grossed',
    }

    /**
     * type of slide: brightfield or fluorescence
     */
    export enum slide_type {
        BRIGHTFIELD = 'brightfield',
        FLUORESCENCE = 'fluorescence',
    }


}
